import { useCallback, useEffect } from "react";

import { useTranslate } from "helpers";
import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
  useHandleDateChange,
} from "services/forms/hooks";
import useUrlFilters from "services/hooks/useUrlFilters";
import { UsageSearchFormValues } from "services/api/finance/usage/types";
import { initialUsageSearchForm, USAGE_KEY_LABELS } from "services/api/finance/usage/consts";

import { UsageSearchFiltersProps } from "./types";

/** Prazna vrsta pokazuje svu potrošnju; odabir je svodi na jednu stavku cjenika. */
const USAGE_KEY_OPTIONS: DropdownOption[] = [
  { value: "", text: "Sve vrste" },
  ...Object.entries(USAGE_KEY_LABELS).map(([value, text]) => ({ value, text })),
];

const SUCCESS_OPTIONS: DropdownOption[] = [
  { value: "", text: "Sve" },
  { value: "true", text: "Uspješno" },
  { value: "false", text: "Nije uspjelo" },
];

const UsageSearchFilters: React.FC<UsageSearchFiltersProps> = ({ onSearchChange }) => {
  const { t } = useTranslate();

  // Razdoblje i vrsta idu u URL, da se pogled može podijeliti i osvježiti bez gubitka filtra.
  const [formValues, setFormValues] = useUrlFilters<UsageSearchFormValues>(
    initialUsageSearchForm,
    {
      pricingKeyCode: { param: "vrsta" },
      dateFrom: { param: "dateFrom", type: "date" },
      dateTo: { param: "dateTo", type: "date-end" },
    }
  );

  useEffect(() => {
    onSearchChange(formValues);
  }, [formValues]);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialUsageSearchForm);
  }, [setFormValues]);

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="w-[180px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleTextFieldChange}
          placeholder="Pretraži po predmetu..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>

      <div className="w-[180px]">
        <Dropdown
          name="pricingKeyCode"
          value={formValues.pricingKeyCode}
          options={USAGE_KEY_OPTIONS}
          onChange={handleDropdownChange}
          placeholder="Vrsta"
          width="full"
        />
      </div>

      <div className="w-[130px]">
        <DatePicker
          name="dateFrom"
          label="Razdoblje od - do"
          value={formValues.dateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>

      <div className="w-[130px]">
        <DatePicker
          name="dateTo"
          value={formValues.dateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>

      <div className="w-[140px]">
        <Dropdown
          name="isSuccess"
          value={formValues.isSuccess}
          options={SUCCESS_OPTIONS}
          onChange={handleDropdownChange}
          placeholder="Rezultat"
          width="full"
        />
      </div>

      {/*
        Ista oznaka kao na pretrazi vozila. Visina polja, a ne `items-center`, jer red poravnava
        prema donjem rubu polja ispod oznake razdoblja.
      */}
      <div className="h-[46px] flex items-center ml-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default UsageSearchFilters;
